/**
 * "Family ledgers" in the command palette (Ctrl+K): type a name, chart number
 * or four phone digits and open that family's ledger. Searches the same
 * audited `/v1/ledger/accounts` list the Billing page uses, so balances in
 * the palette are live.
 */
import { getLedgerAccounts } from '@velora/sdk';
import { clientFor, registerSearchProvider, type SearchProvider } from '../../app/shell.js';
import { money } from './format.js';

/** Client-side navigation from outside React (the palette runs results directly). */
export function goTo(path: string): void {
  if (typeof window === 'undefined') return;
  const url = new URL(path, window.location.origin);
  const office = new URLSearchParams(window.location.search).get('office');
  if (office && !url.searchParams.has('office')) url.searchParams.set('office', office);
  window.history.pushState(window.history.state, '', `${url.pathname}${url.search}`);
  window.dispatchEvent(new PopStateEvent('popstate', { state: window.history.state }));
}

export const ledgerPath = (guarantorId: string) =>
  `/billing?tab=ledger&account=${encodeURIComponent(guarantorId)}`;

export function ledgerSearchProvider(navigate: (path: string) => void = goTo): SearchProvider {
  return {
    id: 'ledger',
    heading: 'Family ledgers',
    order: 30,
    patients: false,
    minLength: 2,
    permission: 'ledger.view',
    search: async (q, { signal, office }) => {
      const res = await getLedgerAccounts(clientFor(office), {
        query: { q, show: 'all', sort: 'name', limit: 6 },
        signal,
      });
      return res.rows.map((r) => ({
        id: `ledger:${r.guarantorId}`,
        label: `${r.name} ledger`,
        hint:
          r.netCents < 0
            ? `Credit ${money(-r.netCents)}`
            : `Balance ${money(r.netCents)}${r.memberCount > 1 ? ` · ${r.memberCount} in family` : ''}`,
        icon: 'wallet',
        keywords: ['ledger', 'balance', 'account'],
        phi: true,
        run: () => navigate(ledgerPath(r.guarantorId)),
      }));
    },
  };
}

let installed = false;

export function installLedgerSearch(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  registerSearchProvider(ledgerSearchProvider());
}
